import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useSEO } from "../hooks/useSEO";

function formatNumber(n) {
  if (n == null) return "—";
  if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(".0", "") + "K";
  return String(n);
}

export default function ArtistProfile() {
  const { slug } = useParams();
  const [artist, setArtist] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useSEO(artist ? artist.name : null, artist ? `${artist.name} — Nominé·e au Stand d'Or 2026. ${artist.bio || ""}` : null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/artistes/${slug}`);
        setArtist(data);
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-[180px] max-w-[1200px] mx-auto px-6 lg:px-12" data-testid="artist-not-found">
        <p className="label" style={{ color: "#C9A961" }}>404</p>
        <h1 className="font-serif mt-6" style={{ fontSize: 72, color: "#F8F6F0" }}>Profil introuvable.</h1>
        <Link to="/nominations" className="link-editorial mt-10 inline-block">Retour aux nominations</Link>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="pt-[180px] max-w-[1200px] mx-auto px-6 lg:px-12">
        <p className="label">Chargement…</p>
      </div>
    );
  }

  return (
    <div data-testid="artist-page">
      {/* Hero */}
      <section className="pt-[120px] pb-20 relative overflow-hidden">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-6">
            <div className="overflow-hidden" style={{ aspectRatio: "3 / 4", background: "#111", maxWidth: 600, position: "relative" }}>
              <img src={artist.portrait} alt={artist.name} className="bw-image w-full h-full object-cover" />
              {artist.portrait_credit && (
                <span style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "6px 12px", background: "rgba(0,0,0,0.52)", fontFamily: "Inter, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.38)", display: "block" }}>
                  © {artist.portrait_credit}
                </span>
              )}
            </div>
          </div>
          <div className="md:col-span-6">
            <p className="label mb-6" style={{ color: "#C9A961" }}>Nommé·e — Édition 2026</p>
            <h1
              className="font-serif"
              style={{ fontSize: "clamp(56px, 9vw, 140px)", lineHeight: 0.92, letterSpacing: "-0.03em", color: "#F8F6F0" }}
              data-testid="artist-name"
            >
              {artist.name}
            </h1>
            <p className="font-serif italic mt-6" style={{ fontSize: "clamp(22px, 2.6vw, 32px)", color: "#C9A961" }}>
              « {artist.work} »
            </p>
            <p className="label mt-3">{artist.work_type}</p>
          </div>
        </div>
      </section>

      {/* Bio */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-12 py-24" style={{ borderTop: "1px solid #1c1c1c" }}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-3">
            <p className="label">Portrait</p>
          </div>
          <div className="md:col-span-8 md:col-start-5">
            <p
              className="font-serif"
              style={{
                fontSize: "clamp(24px, 2.6vw, 34px)",
                lineHeight: 1.4,
                color: "#F8F6F0",
                fontStyle: "italic",
              }}
            >
              {artist.bio}
            </p>
            <p className="mt-10" style={{ color: "rgba(248,246,240,0.75)", fontSize: 17, lineHeight: 1.85 }}>
              {artist.long_bio}
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section
        className="max-w-[1200px] mx-auto px-6 lg:px-12 py-24"
        style={{ borderTop: "1px solid #1c1c1c" }}
      >
        <p className="label mb-12" style={{ color: "#C9A961" }}>Présence</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-testid="artist-stats">
          <div>
            <p className="font-serif" style={{ fontSize: "clamp(56px, 6vw, 96px)", lineHeight: 1, color: "#F8F6F0" }}>
              {formatNumber(artist.stats?.instagram_followers)}
            </p>
            <p className="label mt-4">Abonnés Instagram</p>
          </div>
          <div>
            <p className="font-serif" style={{ fontSize: "clamp(56px, 6vw, 96px)", lineHeight: 1, color: "#F8F6F0" }}>
              {formatNumber(artist.stats?.tiktok_followers)}
            </p>
            <p className="label mt-4">Abonnés TikTok</p>
          </div>
          <div>
            <p className="font-serif" style={{ fontSize: "clamp(56px, 6vw, 96px)", lineHeight: 1, color: "#F8F6F0" }}>
              {formatNumber(artist.stats?.youtube_subscribers)}
            </p>
            <p className="label mt-4">Abonnés YouTube</p>
          </div>
        </div>
      </section>

      {/* Socials */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-12 py-24" style={{ borderTop: "1px solid #1c1c1c" }}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-3">
            <p className="label">Retrouver l'artiste</p>
          </div>
          <div className="md:col-span-8 md:col-start-5 flex flex-wrap gap-10">
            {artist.socials?.instagram && (
              <a className="link-editorial label" href={`https://instagram.com/${artist.socials.instagram.replace("@", "")}`} target="_blank" rel="noopener noreferrer">
                INSTAGRAM
              </a>
            )}
            {artist.socials?.tiktok && (
              <a className="link-editorial label" href={`https://tiktok.com/${artist.socials.tiktok}`} target="_blank" rel="noopener noreferrer">
                TIKTOK
              </a>
            )}
            {artist.socials?.youtube && (
              <a className="link-editorial label" href={`https://youtube.com/@${artist.socials.youtube}`} target="_blank" rel="noopener noreferrer">
                YOUTUBE
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Other nominations */}
      {artist.other_nominations && artist.other_nominations.length > 0 && (
        <section className="max-w-[1200px] mx-auto px-6 lg:px-12 py-24" style={{ borderTop: "1px solid #1c1c1c" }}>
          <p className="label mb-10" style={{ color: "#C9A961" }}>Autres nominations 2026</p>
          <ul>
            {artist.other_nominations.map((o) => (
              <li
                key={o.id}
                className="py-6 flex items-baseline justify-between gap-8"
                style={{ borderBottom: "1px solid #1c1c1c" }}
              >
                <span className="font-serif" style={{ fontSize: 28, color: "#F8F6F0" }}>
                  {o.category_slug.replaceAll("-", " ")}
                </span>
                <Link to={`/voter?category=${o.category_slug}&nominee=${o.id}`} className="label" style={{ color: "#C9A961" }}>
                  Voter →
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
